import { useEffect, useState } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import DashboardLayout from "@/components/DashboardLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Shield } from "lucide-react";

import { OverviewTab } from "./superadmin/OverviewTab";
import { TenantsTab } from "./superadmin/TenantsTab";
import { PlansTab } from "./superadmin/PlansTab";
import { UsageTab } from "./superadmin/UsageTab";
import { ObservabilityTab } from "./superadmin/ObservabilityTab";
import { EmailTab } from "./superadmin/EmailTab";
import { AuditTab } from "./superadmin/AuditTab";
import { HealthTab } from "./superadmin/HealthTab";
import { WebhooksTab } from "./superadmin/WebhooksTab";

type Tab =
  | "overview" | "tenants" | "plans" | "usage"
  | "observability" | "webhooks" | "email" | "audit" | "health";

export default function SuperAdmin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState<Tab>("overview");

  // Sync tab com query string para deep-links e back/forward
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("tab") as Tab | null;
    if (t && ["overview","tenants","plans","usage","observability","webhooks","email","audit","health"].includes(t)) {
      setTab(t);
    }
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) { window.location.href = "/login"; return; }
    if (!user.isSuperAdmin) { window.location.href = "/"; return; }
  }, [user, loading]);

  if (loading || !user?.isSuperAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const onTabChange = (v: string) => {
    setTab(v as Tab);
    const url = new URL(window.location.href);
    url.searchParams.set("tab", v);
    window.history.replaceState(null, "", url.toString());
  };

  return (
    <DashboardLayout>
      <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-[1400px] mx-auto space-y-5">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Shield className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Super Admin</h1>
            <p className="text-sm text-muted-foreground">Cockpit do Lead Conversa — empresas, uso, observabilidade e auditoria.</p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={onTabChange}>
          <TabsList className="flex flex-wrap h-auto">
            <TabsTrigger value="overview">Visão Geral</TabsTrigger>
            <TabsTrigger value="tenants">Empresas</TabsTrigger>
            <TabsTrigger value="plans">Planos & Receita</TabsTrigger>
            <TabsTrigger value="usage">Uso & Quotas</TabsTrigger>
            <TabsTrigger value="observability">Observabilidade</TabsTrigger>
            <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
            <TabsTrigger value="email">E-mail</TabsTrigger>
            <TabsTrigger value="audit">Auditoria</TabsTrigger>
            <TabsTrigger value="health">Saúde</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6"><OverviewTab /></TabsContent>
          <TabsContent value="tenants" className="mt-6"><TenantsTab /></TabsContent>
          <TabsContent value="plans" className="mt-6"><PlansTab /></TabsContent>
          <TabsContent value="usage" className="mt-6"><UsageTab /></TabsContent>
          <TabsContent value="observability" className="mt-6"><ObservabilityTab /></TabsContent>
          <TabsContent value="webhooks" className="mt-6"><WebhooksTab /></TabsContent>
          <TabsContent value="email" className="mt-6"><EmailTab /></TabsContent>
          <TabsContent value="audit" className="mt-6"><AuditTab /></TabsContent>
          <TabsContent value="health" className="mt-6"><HealthTab /></TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
